import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Building2, Hammer } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const StructuralRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/structural-repair"]?.title} description={seoData["services/structural-repair"]?.description} path="/services/structural-repair" keywords={seoData["services/structural-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Concrete Rehabilitation & Structural Repair"
        description="Concrete rehabilitation contractor serving California. Structural concrete repair, spall repair, epoxy concrete repair, concrete crack injection, concrete resurfacing. Infrastructure rehabilitation contractor for municipal and industrial facilities."
        backgroundKey="structural-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Concrete spalling and rebar exposure from corrosion and weathering',
              'Structural cracks from settlement, loading, and thermal cycling',
              'Concrete carbonation and chloride-induced rebar corrosion',
              'Erosion and chemical attack on concrete surfaces',
              'Expansion joint failure and water infiltration',
              'Metal-to-concrete interface corrosion and debonding'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-stone-50 rounded-lg">
                <Building2 className="text-stone-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Hammer className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Spall Repair & Concrete Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Spall repair using Belzona 4111, 4131, and 4154 concrete repair systems. Remove deteriorated concrete, treat exposed rebar, and rebuild to original profile with epoxy-modified repair mortars that bond permanently to existing concrete.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Hammer className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Crack Injection & Structural Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Epoxy and polyurethane crack injection for structural concrete repair. Low-pressure injection systems for crack widths from hairline to 1/2 inch. Restore structural integrity and prevent water infiltration.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Hammer className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Concrete Resurfacing & Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Protective coatings and resurfacing systems for concrete floors, walls, ceilings, and infrastructure. Chemical-resistant coatings for wastewater structures, containment areas, and process environments.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-stone-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Hammer className="text-stone-600 mr-3 flex-shrink-0" size={22} />
                  Metal Loss Rebuild to T-Min
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Structural steel and metal rebuild using Belzona metal repair composites. Restore corroded steel members, baseplates, and embedded metals to required minimum thickness for structural integrity.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Infrastructure rehabilitation contractor with 25+ years experience',
                  'Structural concrete repair for bridges, buildings, tanks, and infrastructure',
                  'Epoxy injection for crack repair without demolition or replacement',
                  'Belzona 4000 series concrete repair systems with permanent bond',
                  'Certified for work on municipal, industrial, and federal facilities',
                  'On-site testing: bond strength, compressive strength, moisture testing',
                  'Full engineering documentation for structural repairs',
                  'Extend infrastructure life and defer costly replacement projects'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4111 (Magma-Quartz)</p>
                  <p className="text-xs text-slate-500 mt-1">Thick-section concrete repair and rebuilding</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4131 (Magma-Screed)</p>
                  <p className="text-xs text-slate-500 mt-1">Concrete resurfacing and floor repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4154 (Magma-CR4)</p>
                  <p className="text-xs text-slate-500 mt-1">Chemical-resistant concrete repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5811 (Immersion Grade)</p>
                  <p className="text-xs text-slate-500 mt-1">Concrete tank and basin protective coating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Deteriorating Concrete? We Rehabilitate It.</h2>
          <p className="text-blue-100 mb-8">Contact our concrete rehabilitation specialists for a free structural assessment. We serve municipalities, water districts, and industrial facilities across California.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StructuralRepair;
